import { Button, Checkbox, Form, Input, InputRef, Modal, ModalProps, Select, Switch } from 'antd'
import DollarInput from '../common/DollarInput'
import { ItemClasses } from '../../common/constants'
import { useSaveCatalogItemMutation, useItemCategories, useItemTags } from './apiQueries'
import { CatalogItem } from '../../domain/catalogItem'
import { useEffect, useRef } from 'react'
import { CatalogItemFormValues } from './catalogItemModel'


export type Props = {
  visible: boolean
  onCreated?: (catalogItem: CatalogItem) => void
  onCancel?: () => void
} & Pick<ModalProps, 'getContainer'>

export const AddItemForm = ({ visible, getContainer, onCreated, onCancel }: Props) => {
  const createCatalogItemMutation = useSaveCatalogItemMutation()
  const itemCategories = useItemCategories(visible)
  const itemTags = useItemTags(visible)

  const [form] = Form.useForm<CatalogItemFormValues>()
  const firstInputRef = useRef<InputRef>(null)
  form.scrollToField('name')

  useEffect(() => {
    if (visible) {
      setTimeout(() => firstInputRef.current?.select(), 1)
    }
  }, [visible])

  const itemClasses = [
    { label: 'Labor', value: ItemClasses.Labor },
    { label: 'Equipment / Vehicles', value: ItemClasses.Equipment },
    { label: 'Materials', value: ItemClasses.Materials },
    { label: 'Subcontracting', value: ItemClasses.Subcontracting },
  ]

  const initialValues: CatalogItemFormValues = {
    name: 'New Item',
    cost: 0,
    taxable: false,
    saveToCatalog: true,
    itemClass: ItemClasses.Materials,
    categories: [],
    tags: []
  }

  const handleSubmit = (values: CatalogItemFormValues) => {
    createCatalogItemMutation.mutate({ catalogItem: values }, {
      onSuccess: (catalogItem: CatalogItem) => {
        form.resetFields()
        onCreated?.(catalogItem)
      }
    })
  }

  const handleCancel = () => {
    form.resetFields()
    onCancel?.()
  }

  return <Modal
    open={visible}
    getContainer={getContainer}
    onCancel={handleCancel}
    title="New Catalog Item"
    footer={[
      <Button onClick={handleCancel} key="cancel">Cancel</Button>,
      <Button type="primary" onClick={form.submit} key="create">Create Item</Button>
    ]}
  >
    <Form
      form={form}
      labelCol={{ span: 4 }}
      wrapperCol={{ span: 20 }}
      initialValues={initialValues}
      onFinish={handleSubmit}
      style={{ marginTop: 24 }}
    >
      <Form.Item label="Name"
        name="name"
        rules={[
          { required: true, message: 'Name is required' },
          { type: 'string', max: 150, message: 'Name cannot be longer than 150 characters' }
        ]}
      >
        <Input placeholder="Name" ref={firstInputRef} maxLength={150} />
      </Form.Item>
      <Form.Item label="Cost" name="cost">
        <DollarInput onChange={() => { }} placeholder="Cost" style={{ width: '100%' }} min={0} />
      </Form.Item>
      <Form.Item label="Taxable" name="taxable" valuePropName="checked">
        <Switch />
      </Form.Item>
      <Form.Item label="Class" name="itemClass" rules={[{ required: true, message: 'Class is required' }]}>
        <Select options={itemClasses} popupMatchSelectWidth={false} />
      </Form.Item>
      <Form.Item label="Units" name="units" rules={[{ type: 'string', max: 50, message: 'Units cannot be longer than 50 characters' }]}>
        <Input placeholder="Units" maxLength={50} />
      </Form.Item>
      <Form.Item label="SKU" name="sku" rules={[{ type: 'string', max: 25, message: 'SKU cannot be longer than 25 characters' }]}>
        <Input placeholder="SKU" maxLength={25} />
      </Form.Item>
      <Form.Item name="categories" label="Categories">
        <Select mode="multiple" options={itemCategories.data?.map(x => ({ label: x.name, value: x.name }))} />
      </Form.Item>
      <Form.Item name="tags" label="Tags">
        <Select mode="tags" options={itemTags.data?.map(x => ({ label: x, value: x }))} />
      </Form.Item>
      <Form.Item name="saveToCatalog" wrapperCol={{ offset: 4, span: 20 }} valuePropName="checked">
        <Checkbox>Save this item to the Item Catalog for future use</Checkbox>
      </Form.Item>
    </Form>
  </Modal>
}

export default AddItemForm
